import { html, TemplateResult, LitElement } from 'lit';
import { customElement, property, queryAssignedElements } from 'lit/decorators.js';
import globalStyles from './styles/outline-scaling-grid.global.css?inline';
import encapsulatedStyles from './styles/outline-scaling-grid.encapsulated.css?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { classMap } from 'lit/directives/class-map.js';
import {debounce } from '../../utilities/debounce';
/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-scaling-grid';

/**
 * The Outline Scaling Grid component
 *
 *
 * @element outline-scaling-grid
 * @extends LitElement
 */
@customElement(componentName)
export class OutlineScalingGrid extends LitElement {
  private _seed = Math.floor(Math.random() * 100000).toString();
  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

   /**
   * Vertical gap between items.
   */
   @property({
    type: String,
    reflect: true,
    attribute: 'row-gap',
  })
  rowGap = '2rem';

  /**
   * Horizontal gap between items.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'col-gap',
  })
  colGap = '2rem';

  /**
   * Maximum amount of columns.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'columns',
  })
  columns = '3';

  /**
   * Minimum width of each item.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'item-min-width',
  })
  itemMinWidth = '200px';

  /**
   * Vertical alignment of each item.
   */
  @property({
    type: String,
    reflect: false,
    attribute: 'align-items',
  })
  alignItems = '';

  /**
   * Horizontal alignment of each item.
   */
  @property({
    type: String,
    reflect: false,
    attribute: 'justify-items',
  })
  justifyItems = '';

    /**
   * Custom wrapper class.
   */
    @property({
      type: String,
      reflect: false,
      attribute: 'class',
    })
    wrapperClass = '';

    
  /**
   * Whether or not all rows will have equal height.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'equal-height',
  })
  equalHeight:boolean;


  @property({type: Boolean, reflect: true, attribute: 'one-col'})
  oneCol:boolean;

  @property({type: Boolean, attribute: 'bordered-content'})
  borderedContent:boolean;

  @property({type: String, attribute: true})
  id = `grid-${this._seed}`

  @queryAssignedElements()
  contentRef: Array<HTMLElement>

  _minWidthOf2Columns: number;

  connectedCallback() {
    super.connectedCallback();
    this._minWidthOf2Columns =
      this.convertRemToPx(this.itemMinWidth) * 2 +
      this.convertRemToPx(this.colGap);
    this.setAttribute('id', `grid-${this._seed}`);
    if (this.equalHeight) {
      window.addEventListener('resize', debounce(() => this.handleEqualHeightResize(), 200));
    }

  }

  firstUpdated() {
    this.handleEqualHeightResize();
    if (this.borderedContent) {
      this.applyBorderClass();
    }
  }

  applyBorderClass() {    
    this.contentRef.map(el => {
      if(this.contentRef.indexOf(el) !== this.contentRef.length -1 ) {
        
        el.classList.add('border');
      }
    })
  }


  handleEqualHeightResize(){    
    const self = this.shadowRoot?.querySelector('.grid-wrapper') as HTMLElement;
    if(self) {
      this._minWidthOf2Columns > self.clientWidth ? this.oneCol = true : this.oneCol = false;    
    }
  }

  convertRemToPx(item: string) {
    if (item.endsWith('rem')) {
      return parseFloat(item) * 16;
    } else {
      return parseInt(item, 10);
    }
  }

  updated() {
    // Set css vars from properties.
    this.setVars();
  }
  /**
   * Set css vars without injecting an inline <style>.
   */
  setVars() {
    this.style.setProperty('--grid-layout-column-gap', `${this.colGap}`);
    this.style.setProperty('--grid-layout-row-gap', `${this.rowGap}`);
    this.style.setProperty('--grid-column--max-count', `${this.columns}`);
    this.style.setProperty('--grid-item--min-width', `${this.itemMinWidth}`);
    this.style.setProperty('--align-items', `${this.alignItems || 'auto'}`);
    this.style.setProperty('--justify-items', `${this.justifyItems || 'auto'}`);
  }

  render(): TemplateResult {
    const wrapperClasses = {
      'grid-wrapper': true,
      'align-items': this.alignItems || '',
      'justify-items': this.justifyItems || '',
      'equal-height': this.equalHeight || '',
      'featured-content': this.wrapperClass || '',
    };
    return html`<div class="${classMap(wrapperClasses)}">
      <slot></slot>
    </div>`;
  }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineScalingGrid;
  }
}
